import { useCallback, useEffect, useMemo, useState } from "react";

import { UseFormReturn } from "react-hook-form";

import { FeatureType } from "src/clients/features-control";
import {
  autoAssignColumnTypes,
  ColumnType,
  getEntityColumnFromTypes,
  handleFeatureGenerationFromColumnTypes,
  MIN_COLUMNS_FOR_NON_GLOBAL_FEATURES,
  PreviewData,
} from "src/featureCatalogue/Queries/Create/helpers";
import { QueryFormValues } from "src/featureCatalogue/Queries/Create/schema";
import { inferDataTypes } from "src/featureCatalogue/Queries/SQLResonsePreviewTable";

export const useFeatureAutoMapping = (
  form: UseFormReturn<QueryFormValues>,
  isEditMode: boolean = false,
) => {
  const [columnTypes, setColumnTypes] = useState<Record<string, ColumnType>>(
    {},
  );
  const [featureDatatype, setFeatureDatatype] = useState<
    Record<string, FeatureType | undefined>
  >({});

  const windowLength = form.watch("window_length");
  const entityName = form.watch("columns.entities.0.entity_name");

  const updatedColumnTypes = useMemo(() => {
    if (Object.keys(columnTypes).length === 0) return columnTypes;
    const hasEntity = Boolean(entityName);
    const dummyRow = Object.fromEntries(
      Object.keys(columnTypes).map((col) => [col, null]),
    );
    return autoAssignColumnTypes([dummyRow], columnTypes, hasEntity);
  }, [columnTypes, entityName]);

  useEffect(() => {
    if (Object.keys(updatedColumnTypes).length > 0) {
      const entityName = form.getValues("columns.entities.0.entity_name");
      const existingFeatures = form.getValues("columns.features") || [];

      // Apply entity column
      const entityColumn = getEntityColumnFromTypes(updatedColumnTypes);
      if (entityColumn && entityName) {
        form.setValue("columns.entities.0.column_name", entityColumn);
      } else if (!entityName) {
        form.setValue("columns.entities.0.column_name", "");
      }

      // Apply grouping key — only one is allowed.
      const groupingKeyColumn = Object.entries(updatedColumnTypes).find(
        ([_, type]) => type === "grouping_key",
      )?.[0];
      form.setValue(
        "columns.grouping_keys",
        groupingKeyColumn ? [groupingKeyColumn] : [],
      );

      // Generate and apply features
      const features = handleFeatureGenerationFromColumnTypes(
        updatedColumnTypes,
        existingFeatures,
        windowLength,
        featureDatatype,
      );
      form.setValue("columns.features", features);
    }
  }, [updatedColumnTypes, form, featureDatatype, windowLength]);

  const setColumnType = useCallback((column: string, type: ColumnType) => {
    setColumnTypes((prev) => ({ ...prev, [column]: type }));
  }, []);

  const handleFeatureGeneration = useCallback(
    (previewData: PreviewData): boolean => {
      const columnCount = Object.keys(previewData?.at(0) ?? {}).length;
      // Single column queries cannot have an entity — clear it so all columns become features
      if (columnCount < MIN_COLUMNS_FOR_NON_GLOBAL_FEATURES && !isEditMode) {
        form.setValue("columns.entities.0.entity_name", "");
      }

      const hasEntity = Boolean(
        form.getValues("columns.entities.0.entity_name"),
      );
      setFeatureDatatype(inferDataTypes(previewData));
      const newColumnTypes = autoAssignColumnTypes(previewData, {}, hasEntity);
      setColumnTypes(newColumnTypes);
      return Object.values(newColumnTypes).some((type) => type === "feature");
    },
    [form.getValues, isEditMode],
  );

  return {
    columnTypes: updatedColumnTypes,
    setColumnType,
    handleFeatureGeneration,
  };
};
